import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Search, Download, Refresh } from "@mui/icons-material";
import axios from "axios";
import { format } from "date-fns";
import { debounce } from "lodash";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useAccountsNav } from "../../utils/moduleNav";

import { useLedgerState } from "../../Context/LedgerStateContext";
import { useLocation } from "react-router-dom";

// ── SESSION KEY ──
const LEDGER_SESSION_KEY = "individualLedger_state";

const clearLedgerSession = () => {
  try {
    sessionStorage.removeItem(LEDGER_SESSION_KEY);
  } catch {}
};

const IndividualLedger = ({ tabIndex }) => {
  // Keeps the voucher drill-down inside the host module (pawn keeps its chrome).
  const { go: goAccounts } = useAccountsNav();
  const location = useLocation();
  const { userInfo, allStores, defaultFY, defaultFYId } =
    useContext(AutoTaskAuthContext) || {};
  const { ledgerState, updateTabState } = useLedgerState();
  const store_id = userInfo?.store_id;

  const isReturningFromEntry = location.state?.fromAccountsEntry === true;
  const highlightVoucher = location.state?.highlightVoucher || null;

  const savedState = isReturningFromEntry ? ledgerState.individualLedger : null;

  // ── Ref to scroll highlighted row into view ──
  const highlightRowRef = useRef(null);

  const [filterType, setFilterType] = useState(
    savedState?.filterType ?? "Account",
  );
  const [selectedAccount, setSelectedAccount] = useState(
    savedState?.selectedAccount ?? null,
  );
  const [selectedSubAccount, setSelectedSubAccount] = useState(
    savedState?.selectedSubAccount ?? null,
  );
  const [selectedParty, setSelectedParty] = useState(
    savedState?.selectedParty ?? null,
  );
  const [partyCodeInput, setPartyCodeInput] = useState(
    savedState?.partyCodeInput ?? "",
  );
  const [partyNameInput, setPartyNameInput] = useState(
    savedState?.partyNameInput ?? "",
  );
  const [partyMobileInput, setPartyMobileInput] = useState(
    savedState?.partyMobileInput ?? "",
  );
  const [fromDate, setFromDate] = useState(
    savedState?.fromDate ??
      format(
        new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        "yyyy-MM-dd",
      ),
  );
  const [toDate, setToDate] = useState(
    savedState?.toDate ?? format(new Date(), "yyyy-MM-dd"),
  );
  const [useFY, setUseFY] = useState(savedState?.useFY ?? false);

  const [accounts, setAccounts] = useState([]);
  const [allCostCenters, setAllCostCenters] = useState([]);
  const [filteredCostCenters, setFilteredCostCenters] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);

  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingCostCenters, setLoadingCostCenters] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);

  const [ledgerData, setLedgerData] = useState(savedState?.ledgerData ?? null);
  const [error, setError] = useState("");

  // ── Scroll to highlighted row after data loads ──
  useEffect(() => {
    if (highlightVoucher && ledgerData && highlightRowRef.current) {
      setTimeout(() => {
        highlightRowRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 150);
    }
  }, [highlightVoucher, ledgerData]);

  const getFYLabel = () => {
    if (!defaultFY) return "Use FY";
    return `FY ${defaultFY}-${String(defaultFY + 1).slice(-2)}`;
  };

  const fillPartyFields = (customer) => {
    setSelectedParty(customer);
    setPartyCodeInput(customer.party_code || "");
    setPartyNameInput(customer.name || "");
    setPartyMobileInput(customer.mobile?.toString() || "");
  };

  const clearPartyFields = () => {
    setSelectedParty(null);
    setPartyCodeInput("");
    setPartyNameInput("");
    setPartyMobileInput("");
  };

  const fetchPartyByCode = async (code) => {
    if (!code?.trim()) return;
    try {
      setLoadingCustomers(true);
      const res = await axios.get(
        `${
          process.env.REACT_APP_API_BASE_URL
        }/api/travel/customers/store/${store_id}/party/${code.trim()}`,
      );
      if (res.data.success) {
        fillPartyFields(res.data.data);
      } else {
        setError("No customer found with this party code");
      }
    } catch {
      setError("Failed to fetch customer by party code");
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchPartyByMobile = async (mobile) => {
    const clean = mobile?.toString().replace(/\D/g, "") || "";
    if (clean.length < 6) return;
    try {
      setLoadingCustomers(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/travel/customers/store/${store_id}/phone/${clean}`,
      );
      if (res.data.success) {
        fillPartyFields(res.data.data);
      } else {
        setError("No customer found with this mobile number");
      }
    } catch {
      setError("Failed to fetch customer by mobile");
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/accounts/accounts/${store_id}/accounts-only`,
      );
      if (response.data.success) {
        setAccounts(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setError("Error loading accounts");
    } finally {
      setLoadingAccounts(false);
    }
  };

  // ✅ Fetch & parse cost centers (supports acc_nos JSON array)
  const fetchCostCenters = async () => {
    try {
      setLoadingCostCenters(true);
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/accounts/cost-centers/${store_id}`;
      const res = await axios.get(url);
      if (res.data?.success) {
        const parsed = (res.data.data || []).map((cc) => ({
          ...cc,
          acc_nos: (() => {
            if (Array.isArray(cc.acc_nos)) return cc.acc_nos;
            if (typeof cc.acc_nos === "string") {
              try {
                return JSON.parse(cc.acc_nos);
              } catch {
                return [];
              }
            }
            return [];
          })(),
        }));
        setAllCostCenters(parsed);
      }
    } catch (error) {
      console.error("Error fetching cost centers:", error);
    } finally {
      setLoadingCostCenters(false);
    }
  };

  const searchCustomers = useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.trim().length < 2) {
        setCustomerOptions([]);
        return;
      }
      if (store_id === null || store_id === undefined) return;

      try {
        setLoadingCustomers(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/travel/customers/search/${store_id}`,
          { params: { q: searchTerm.trim(), limit: 50 } },
        );
        if (response.data.success) {
          setCustomerOptions(response.data.data || []);
        } else {
          setCustomerOptions([]);
        }
      } catch (error) {
        console.error("Error searching customers:", error);
        setCustomerOptions([]);
        setError("Error searching customers");
      } finally {
        setLoadingCustomers(false);
      }
    }, 300),
    [store_id],
  );

  useEffect(() => {
    updateTabState(tabIndex, {
      filterType,
      selectedAccount,
      selectedSubAccount,
      selectedParty,
      partyCodeInput,
      partyNameInput,
      partyMobileInput,
      fromDate,
      toDate,
      useFY,
      ledgerData,
    });
  }, [
    filterType,
    selectedAccount,
    selectedSubAccount,
    selectedParty,
    partyCodeInput,
    partyNameInput,
    partyMobileInput,
    fromDate,
    toDate,
    useFY,
    ledgerData,
    tabIndex,
    updateTabState,
  ]);

  useEffect(() => {
    return () => {
      searchCustomers.cancel();
    };
  }, [searchCustomers]);

  useEffect(() => {
    if (store_id !== null && store_id !== undefined) {
      fetchAccounts();
      fetchCostCenters();
    }
  }, [store_id]);

  // ✅ Filter cost centers based on selected account (using acc_nos)
  useEffect(() => {
    setSelectedSubAccount(null);
    if (selectedAccount) {
      const accountId = selectedAccount.account_id;
      const filtered = allCostCenters.filter((cc) => {
        const accNos = Array.isArray(cc.acc_nos) ? cc.acc_nos : [];
        return accNos.includes(accountId);
      });
      setFilteredCostCenters(filtered);
    } else {
      setFilteredCostCenters([]);
    }
  }, [selectedAccount, allCostCenters]);

  const prevFilterType = useRef(filterType);

  useEffect(() => {
    if (prevFilterType.current === filterType) {
      prevFilterType.current = filterType;
      return;
    }
    prevFilterType.current = filterType;

    setSelectedAccount(null);
    setSelectedSubAccount(null);
    setSelectedParty(null);
    setLedgerData(null);
    setError("");
  }, [filterType]);

  const handleGenerateReport = async () => {
    setError("");

    if (filterType === "Account" && !selectedAccount) {
      setError("Please select an account");
      return;
    }
    if (filterType === "Party" && !selectedParty) {
      setError("Please select a party");
      return;
    }
    if (!fromDate || !toDate) {
      setError("Please select date range");
      return;
    }
    if (!defaultFYId) {
      setError("No financial year found. Please contact admin.");
      return;
    }

    try {
      setLoadingReport(true);

      const payload = {
        store_id: parseInt(store_id),
        filter_type: filterType,
        fy_id: defaultFYId,
        from_date: fromDate,
        to_date: toDate,
      };

      if (filterType === "Account") {
        payload.account_id = selectedAccount.account_id;
        if (selectedSubAccount) {
          // ✅ Send both id and name -> backend expects sub_account_id and sub_account_name
          payload.sub_account_id = selectedSubAccount.id;
          payload.sub_account_name = selectedSubAccount.name;
        }
      } else {
        payload.party_account_id =
          selectedParty.customer_id || selectedParty.id;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/accounts/individual-ledger`,
        payload,
      );

      if (response.data.success) {
        setLedgerData(response.data.data);
      } else {
        setError(response.data.message || "Failed to generate report");
      }
    } catch (error) {
      setError(
        error.response?.data?.message || "Error generating ledger report",
      );
    } finally {
      setLoadingReport(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Helper: get cost centre name by ID
  const getCostCentreName = (costCentreId) => {
    if (!costCentreId) return "-";
    const found = allCostCenters.find((cc) => cc.id === costCentreId);
    return found ? found.name : costCentreId.toString();
  };

  const hasPartyCode =
    ledgerData &&
    (filterType === "Party" ||
      selectedAccount?.account_name?.toLowerCase() === "party");

  const generateLedgerPDF = () => {
    if (!ledgerData) return;

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;

    const formatAmount = (amount) => {
      return new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    const storeName = userInfo?.store_name || "";

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(storeName, pageWidth / 2, 12, { align: "center" });

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    let subtitle = "";
    if (filterType === "Account") {
      subtitle = ledgerData.account_details.account_name || "";
      if (selectedSubAccount) {
        subtitle += ` / ${selectedSubAccount.name}`;
      }
    } else {
      subtitle = `${ledgerData.account_details.party_name || ""} ${
        ledgerData.account_details.party_mobile
          ? "- " + ledgerData.account_details.party_mobile
          : ""
      }`;
    }
    doc.text(subtitle, pageWidth / 2, 18, { align: "center" });

    doc.setFontSize(8);
    const statementLine = ledgerData?.date_range?.fy
      ? `FY ${ledgerData.date_range.fy}-${String(
          ledgerData.date_range.fy + 1,
        ).slice(-2)} (${format(
          new Date(ledgerData.date_range.from),
          "dd-MM-yyyy",
        )} to ${format(new Date(ledgerData.date_range.to), "dd-MM-yyyy")})`
      : `Statement Of Account: ${format(
          new Date(fromDate),
          "dd-MM-yyyy",
        )} to ${format(new Date(toDate), "dd-MM-yyyy")}`;
    doc.text(statementLine, margin, 24);
    doc.text("Page No 1of 1", pageWidth - margin - 20, 24);

    const tableHead =
      hasPartyCode && filterType === "Account"
        ? [
            [
              "Date",
              "Party Code",
              "Party Name",
              "Narration",
              "Debit",
              "Credit",
              "Balance",
            ],
          ]
        : [["Date", "Vehicle", "Narration", "Debit", "Credit", "Balance"]];

    const tableData = [];

    const openingBalanceType =
      ledgerData.opening_balance_type === "DEBIT" ? "Db" : "Cr";

    if (hasPartyCode) {
      tableData.push([
        "",
        "",
        "",
        "Opening Balance",
        formatAmount(ledgerData.opening_balance),
        "",
        `${formatAmount(ledgerData.opening_balance)}${openingBalanceType}`,
      ]);
    } else {
      tableData.push([
        "",
        "Opening Balance",
        formatAmount(ledgerData.opening_balance),
        "",
        `${formatAmount(ledgerData.opening_balance)}${openingBalanceType}`,
      ]);
    }

    ledgerData.transactions.forEach((txn) => {
      const dateStr = format(new Date(txn.date), "dd-MM-yyyy");
      const debitStr =
        txn.debit_amount > 0 ? formatAmount(txn.debit_amount) : "";
      const creditStr =
        txn.credit_amount > 0 ? formatAmount(txn.credit_amount) : "";
      const balanceType = txn.balance_type === "DEBIT" ? "Db" : "Cr";
      const balanceStr = `${formatAmount(txn.running_balance)}${balanceType}`;

      if (hasPartyCode) {
        tableData.push([
          dateStr,
          txn.party_code || "",
          txn.party_name || "",
          txn.narration || "",
          debitStr,
          creditStr,
          balanceStr,
        ]);
      } else {
        // For party ledger (filterType === "Party") the "Vehicle" column should show cost centre name
        const vehicleValue = getCostCentreName(txn.costcentreid);
        tableData.push([
          dateStr,
          vehicleValue,
          txn.narration || "",
          debitStr,
          creditStr,
          balanceStr,
        ]);
      }
    });

    if (hasPartyCode) {
      tableData.push([
        "",
        "",
        "",
        "Closing Totals",
        formatAmount(ledgerData.total_debit),
        formatAmount(ledgerData.total_credit),
        "",
      ]);
    } else {
      tableData.push([
        "",
        "Closing Totals",
        formatAmount(ledgerData.total_debit),
        formatAmount(ledgerData.total_credit),
        "",
      ]);
    }

    const debitColIndex = hasPartyCode ? 4 : 2;

    doc.autoTable({
      startY: 28,
      head: tableHead,
      body: tableData,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 1.5,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        textColor: [0, 0, 0],
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
        lineWidth: 0.3,
        fontSize: 9,
      },
      columnStyles: hasPartyCode
        ? {
            0: { halign: "left", cellWidth: 20 },
            1: { halign: "left", cellWidth: 18 },
            2: { halign: "left", cellWidth: 32 },
            3: { halign: "left", cellWidth: 40 },
            4: { halign: "right", cellWidth: 20 },
            5: { halign: "right", cellWidth: 20 },
            6: { halign: "right", cellWidth: 22 },
          }
        : {
            0: { halign: "left", cellWidth: 25 },
            1: { halign: "left", cellWidth: 90 },
            2: { halign: "right", cellWidth: 25 },
            3: { halign: "right", cellWidth: 25 },
            4: { halign: "right", cellWidth: 25 },
          },
      bodyStyles: { textColor: [0, 0, 0] },
      didParseCell: function (data) {
        if (data.row.index === 0 && data.section === "body") {
          data.cell.styles.fontStyle = "bold";
        }
        if (
          data.row.index === tableData.length - 1 &&
          data.section === "body"
        ) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [245, 245, 245];
        }
        if (
          data.column.index === debitColIndex &&
          data.cell.text[0] &&
          data.section === "body" &&
          data.row.index !== tableData.length - 1
        ) {
          data.cell.styles.textColor = [255, 0, 0];
        }
      },
      margin: { left: margin, right: margin },
    });

    const accountName =
      filterType === "Account"
        ? ledgerData.account_details.account_name
        : ledgerData.account_details.party_name;
    const sanitizedAccountName = accountName.replace(/[^a-zA-Z0-9]/g, "_");
    const filename = `Ledger_${sanitizedAccountName}_${format(
      new Date(fromDate),
      "dd-MM-yyyy",
    )}_to_${format(new Date(toDate), "dd-MM-yyyy")}.pdf`;

    doc.save(filename);
  };

  const handleRowDoubleClick = (voucherNo) => {
    if (voucherNo) {
      goAccounts("/accounts-entry", {
        state: { voucher_no: voucherNo, fromLedger: true },
      });
    }
  };

  return (
    <Box>
      {/* Filters Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Report Filters
        </Typography>

        <Grid container spacing={2} alignItems="flex-start">
          {/* Filter Type */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Filter Type</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                label="Filter Type"
              >
                <MenuItem value="Account">Account</MenuItem>
                <MenuItem value="Party">Party</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {filterType === "Account" && (
            <>
              <Grid item xs={12} md={2.5}>
                <Autocomplete
                  options={accounts}
                  getOptionLabel={(option) =>
                    `${option.account_name}${
                      option.account_code ? " - " + option.account_code : ""
                    }`
                  }
                  value={selectedAccount}
                  onChange={(event, newValue) => setSelectedAccount(newValue)}
                  loading={loadingAccounts}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Account"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingAccounts ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={2.5}>
                <Autocomplete
                  options={filteredCostCenters}
                  getOptionLabel={(option) => option.name}
                  value={selectedSubAccount}
                  onChange={(event, newValue) =>
                    setSelectedSubAccount(newValue)
                  }
                  loading={loadingCostCenters}
                  disabled={!selectedAccount}
                  noOptionsText={
                    !selectedAccount
                      ? "Select an account first"
                      : "No sub-accounts found for this account"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sub Account (Optional)"
                      placeholder={
                        !selectedAccount
                          ? "Select account first"
                          : "Select sub account"
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCostCenters ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </>
          )}

          {filterType === "Party" && (
            <>
              <Grid item xs={12} md={1.5}>
                <TextField
                  fullWidth
                  label="Party Code"
                  placeholder="Enter & press ↵"
                  value={partyCodeInput}
                  onChange={(e) => {
                    setPartyCodeInput(e.target.value);
                    if (selectedParty) clearPartyFields();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Tab") {
                      e.preventDefault();
                      fetchPartyByCode(partyCodeInput);
                    }
                  }}
                  InputProps={{
                    endAdornment: loadingCustomers ? (
                      <CircularProgress size={16} />
                    ) : null,
                  }}
                  helperText="Press Tab to lookup"
                />
              </Grid>

              <Grid item xs={12} md={2.5}>
                <Autocomplete
                  options={customerOptions}
                  getOptionLabel={(option) =>
                    `${option.name} - ${option.mobile || "N/A"}`
                  }
                  value={selectedParty}
                  onChange={(event, newValue) => {
                    if (newValue) fillPartyFields(newValue);
                    else clearPartyFields();
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      setPartyNameInput(newInputValue);
                      searchCustomers(newInputValue);
                    }
                  }}
                  inputValue={partyNameInput}
                  loading={loadingCustomers}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Party"
                      placeholder="Type name to search..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCustomers ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  noOptionsText="Type at least 2 characters to search"
                />
              </Grid>

              <Grid item xs={12} md={1.5}>
                <TextField
                  fullWidth
                  label="Mobile"
                  placeholder="Enter & press ↵"
                  value={partyMobileInput}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "");
                    setPartyMobileInput(val);
                    if (selectedParty) clearPartyFields();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Tab") {
                      e.preventDefault();
                      fetchPartyByMobile(partyMobileInput);
                    }
                  }}
                  inputProps={{ maxLength: 15 }}
                  InputProps={{
                    endAdornment: loadingCustomers ? (
                      <CircularProgress size={16} />
                    ) : null,
                  }}
                  helperText="Press Tab to lookup"
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              label="From Date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              label="To Date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              size="large"
              startIcon={
                loadingReport ? <CircularProgress size={20} /> : <Search />
              }
              onClick={handleGenerateReport}
              disabled={loadingReport}
              sx={{ mr: 2 }}
            >
              {loadingReport ? "Generating..." : "Generate Report"}
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<Refresh />}
              onClick={() => {
                clearLedgerSession();
                setSelectedAccount(null);
                setSelectedSubAccount(null);
                setSelectedParty(null);
                setLedgerData(null);
                setError("");
                clearPartyFields();
                setCustomerOptions([]);
              }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Ledger Report */}
      {ledgerData && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Ledger Statement
            </Typography>

            <Typography variant="body1">
              <strong>Period:</strong>{" "}
              {ledgerData.date_range?.fy
                ? `FY ${ledgerData.date_range.fy}-${String(
                    ledgerData.date_range.fy + 1,
                  ).slice(-2)} (${format(
                    new Date(ledgerData.date_range.from),
                    "dd/MM/yyyy",
                  )} – ${format(
                    new Date(ledgerData.date_range.to),
                    "dd/MM/yyyy",
                  )})`
                : `${format(new Date(fromDate), "dd/MM/yyyy")} to ${format(
                    new Date(toDate),
                    "dd/MM/yyyy",
                  )}`}
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">
                  <strong>
                    {filterType === "Account" ? "Account:" : "Party:"}
                  </strong>{" "}
                  {filterType === "Account"
                    ? `${ledgerData.account_details.account_name} (${ledgerData.account_details.account_code})`
                    : `${ledgerData.account_details.party_name} - ${ledgerData.account_details.party_mobile}`}
                </Typography>
                {filterType === "Account" && selectedSubAccount && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    <strong>Sub Account:</strong> {selectedSubAccount.name}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">
                  <strong>Period:</strong>{" "}
                  {format(new Date(fromDate), "dd/MM/yyyy")} to{" "}
                  {format(new Date(toDate), "dd/MM/yyyy")}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Opening Balance
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {formatCurrency(ledgerData.opening_balance)}
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      display: "inline-block",
                      px: 1,
                      py: 0.2,
                      borderRadius: 1,
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "white",
                      backgroundColor: "primary.main",
                    }}
                  >
                    {ledgerData.opening_balance_type}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total Debit
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: "error.main" }}
                  >
                    {formatCurrency(ledgerData.total_debit)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {ledgerData.transaction_count} transactions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total Credit
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    {formatCurrency(ledgerData.total_credit)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {ledgerData.transaction_count} transactions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.100" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Voucher No.</TableCell>
                  {hasPartyCode && filterType === "Account" && (
                    <>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        Party Code
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        Party Name
                      </TableCell>
                    </>
                  )}
                  {filterType === "Party" && (
                    <TableCell colSpan={1} sx={{ fontWeight: 600 }}>
                      Vehicle
                    </TableCell>
                  )}
                  <TableCell
                    colSpan={filterType === "Party" ? 2.5 : 1}
                    sx={{ fontWeight: 600 }}
                  >
                    Narration
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, color: "error.main" }}
                  >
                    Debit
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    Credit
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Balance
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Opening Balance Row */}
                <TableRow sx={{ backgroundColor: "grey.50" }}>
                  <TableCell colSpan={hasPartyCode ? 5 : 3}>
                    <strong>Opening Balance</strong>
                  </TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right">
                    <Typography>
                      {formatCurrency(ledgerData.opening_balance)}{" "}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        ml: 0.5,
                        px: 0.5,
                        py: 0.5,
                        borderRadius: 20,
                        fontSize: "0.6rem",
                        fontWeight: 600,
                        lineHeight: 1,
                        color: "white",
                        backgroundColor: "primary.main",
                      }}
                    >
                      {ledgerData.opening_balance_type}
                    </Box>
                  </TableCell>
                </TableRow>

                {/* Transaction Rows */}
                {ledgerData.transactions.map((txn, index) => {
                  const isHighlighted = highlightVoucher === txn.voucher_number;
                  return (
                    <TableRow
                      key={index}
                      ref={isHighlighted ? highlightRowRef : null}
                      hover
                      onDoubleClick={() =>
                        handleRowDoubleClick(txn.voucher_number)
                      }
                      sx={{
                        cursor: "pointer",
                        backgroundColor: isHighlighted
                          ? "rgba(4, 126, 207, 0.15)"
                          : "transparent",
                        "&:hover": {
                          backgroundColor: isHighlighted
                            ? "rgba(4, 126, 207, 0.25)"
                            : "action.hover",
                        },
                      }}
                    >
                      <TableCell>
                        {format(new Date(txn.date), "dd/MM/yyyy")}
                      </TableCell>
                      <TableCell>{txn.voucher_number}</TableCell>
                      {hasPartyCode && filterType === "Account" && (
                        <>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {txn.party_code || "-"}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {txn.party_name || "-"}
                          </TableCell>
                        </>
                      )}
                      {filterType === "Party" && (
                        <TableCell colSpan={1}>
                          {/* ✅ Show cost centre name instead of ID */}
                          {getCostCentreName(txn.costcentreid)}
                        </TableCell>
                      )}
                      <TableCell colSpan={filterType === "Party" ? 2.5 : 1}>
                        {txn.narration}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "error.main" }}>
                        {txn.debit_amount > 0
                          ? formatCurrency(txn.debit_amount)
                          : "-"}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "text.primary" }}>
                        {txn.credit_amount > 0
                          ? formatCurrency(txn.credit_amount)
                          : "-"}
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatCurrency(txn.running_balance)}</strong>{" "}
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            ml: 0.5,
                            px: 0.5,
                            py: 0.5,
                            borderRadius: 20,
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            lineHeight: 1,
                            color: "white",
                            backgroundColor: "primary.main",
                          }}
                        >
                          {txn.balance_type}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* Closing Balance Row */}
                <TableRow sx={{ backgroundColor: "grey.50" }}>
                  <TableCell colSpan={hasPartyCode ? 5 : 3}>
                    <strong>Closing Balance</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{formatCurrency(ledgerData.total_debit)}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{formatCurrency(ledgerData.total_credit)}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>
                      {formatCurrency(ledgerData.closing_balance)}{" "}
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          ml: 0.5,
                          px: 0.5,
                          py: 0.5,
                          borderRadius: 20,
                          fontSize: "0.6rem",
                          fontWeight: 600,
                          lineHeight: 1,
                          color: "white",
                          backgroundColor: "primary.main",
                        }}
                      >
                        {ledgerData.closing_balance_type}
                      </Box>
                    </strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3, textAlign: "right" }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={generateLedgerPDF}
            >
              Download PDF
            </Button>
          </Box>
        </Paper>
      )}

      {!ledgerData && !error && !loadingReport && (
        <Paper elevation={1} sx={{ p: 6, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            Select filters and click "Generate Report" to view ledger statement
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default IndividualLedger;
